import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  type ScrollHintConfig,
  SHAPE_CLASSES,
  ANIMATION_CLASSES,
  SIZE_CLASSES,
} from '../services/scrollHintSettings';

interface ScrollHintProps {
  /** Référence vers l'élément scrollable */
  targetRef: React.RefObject<HTMLElement>;
  /** Configuration de l'indicateur (depuis l'admin) */
  config: ScrollHintConfig;
  /**
   * Décalage depuis le bas en px. Augmente la valeur si tu as un footer
   * sticky (Total/Commander) pour que la flèche apparaisse AU-DESSUS.
   */
  bottomOffset?: number;
  /** Z-index */
  zIndex?: number;
}

/**
 * v5 — Indicateur de scroll universel.
 *
 * Affiché en bas (centré) de l'élément scrollable, uniquement si :
 * - le contenu dépasse (scrollHeight > clientHeight)
 * - l'utilisateur n'a pas encore commencé à scroller (scrollTop < 8)
 *
 * Le composant utilise un wrapper en position `sticky` qui reste collé
 * en bas du viewport scrollable, donc il ne bouge pas avec le scroll
 * et n'est jamais bloqué par les éléments en bas.
 *
 * Click → scroll smooth vers la position max.
 */
export const ScrollHint: React.FC<ScrollHintProps> = ({
  targetRef,
  config,
  bottomOffset = 12,
  zIndex = 20,
}) => {
  const [visible, setVisible] = useState(false);
  const rafRef = useRef<number | null>(null);

  // Vérifie si la flèche doit être affichée
  const checkScrollState = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;

    const overflows = el.scrollHeight > el.clientHeight + 4;
    const notScrolledYet = el.scrollTop < 8;

    setVisible(overflows && notScrolledYet);
  }, [targetRef]);

  // Listener du scroll + observers
  useEffect(() => {
    const el = targetRef.current;
    if (!el || !config.enabled) {
      setVisible(false);
      return;
    }

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(checkScrollState);
    };

    // Check initial avec léger délai (laisser le DOM se stabiliser)
    setTimeout(checkScrollState, 50);

    el.addEventListener('scroll', onScroll, { passive: true });

    // Observer les changements de taille
    let resizeObs: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObs = new ResizeObserver(() => {
        setTimeout(checkScrollState, 50);
      });
      resizeObs.observe(el);
      Array.from(el.children).forEach((child) => resizeObs!.observe(child));
    }

    // Observer les ajouts/suppressions d'enfants
    const mutObs = new MutationObserver(() => {
      setTimeout(checkScrollState, 50);
    });
    mutObs.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (resizeObs) resizeObs.disconnect();
      mutObs.disconnect();
    };
  }, [targetRef, config.enabled, checkScrollState]);

  const handleClick = () => {
    const el = targetRef.current;
    if (!el) return;
    try {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } catch {
      el.scrollTop = el.scrollHeight;
    }
  };

  if (!config.enabled || !visible) return null;

  const shapeClass = SHAPE_CLASSES[config.shape];
  const animClass = ANIMATION_CLASSES[config.animation];
  const sizeClass = SIZE_CLASSES[config.size];
  const hasText = (config.text || '').trim().length > 0;

  const boxShadow = config.glow
    ? `0 0 16px ${config.bgColor}, 0 0 4px ${config.bgColor}`
    : '0 2px 8px rgba(0,0,0,0.15)';

  // 2 divs imbriqués pour éviter le conflit transform animation / positionnement.
  // - Wrapper externe : reçoit l'animation d'apparition (appear)
  // - Bouton intérieur : reçoit l'animation infinie (bounce/pulse/wiggle/fade)
  // Sinon les 2 animations qui utilisent transform se marchent dessus
  // et l'animation appear (qui termine à translateY(0)) FIGE l'animation infinie.
  return (
    <div
      className="absolute left-0 right-0 flex justify-center pointer-events-none scroll-hint-appear"
      style={{
        bottom: `${bottomOffset}px`,
        zIndex,
      }}
    >
      <button
        type="button"
        onClick={handleClick}
        className={`${shapeClass} ${sizeClass} ${animClass} flex items-center gap-1.5 font-medium pointer-events-auto select-none focus:outline-none cursor-pointer`}
        style={{
          backgroundColor: config.bgColor,
          color: config.textColor,
          border: config.borderColor !== 'transparent' ? `1px solid ${config.borderColor}` : 'none',
          boxShadow,
          whiteSpace: 'nowrap',
        }}
        aria-label={config.text || 'Voir plus'}
      >
        {config.showIcon && <ChevronDown size={hasText ? 14 : 20} strokeWidth={2.5} />}
        {hasText && <span>{config.text}</span>}
      </button>
    </div>
  );
};

export default ScrollHint;
